'use client';

import { useEffect, useRef, useState } from 'react';

// Data fetching logic
interface CityData {
    name: string;
    country: string;
    lat: number;
    lng: number;
    visits: number;
}

export default function GlobalTraderMap() {
    const [allCities, setAllCities] = useState<CityData[]>([]);
    const [selectedCity, setSelectedCity] = useState<CityData | null>(null);

    useEffect(() => {
        const fetchCities = async () => {
            try {
                const response = await fetch('/cities.txt');
                const text = await response.text();
                const lines = text.split('\n').slice(5); // Skip header lines
                const parsedCities: CityData[] = [];

                lines.forEach(line => {
                    // Fixed width approx or regex based on spacing
                    // Line format: "6     Unknown                   Unknown              N/A          N/A          1354       6628       1761"
                    // Regex: \d+\s+(.+?)\s{2,}(.+?)\s{2,}([\d\.-]+|N\/A)\s{2,}([\d\.-]+|N\/A)\s+(\d+)\s+(\d+)
                    
                    const match = line.match(/^\d+\s+(.+?)\s{2,}(.+?)\s{2,}([\d\.-]+|N\/A)\s{2,}([\d\.-]+|N\/A)\s+\d+\s+(\d+)/);
                    if (match) {
                        const lat = parseFloat(match[3]);
                        const lng = parseFloat(match[4]);
                        
                        if (!isNaN(lat) && !isNaN(lng)) {
                            parsedCities.push({
                                name: match[1].trim(),
                                country: match[2].trim(),
                                lat: lat,
                                lng: lng,
                                visits: parseInt(match[5])
                            });
                        }
                    }
                });
                
                // Sort by visits for Z-index layering (smaller on top?) No, larger on top usually or larger behind.
                // Let's sort large first to draw them first (behind) so small ones pop? 
                // actually React render order: last is on top. So sort Small -> Large.
                parsedCities.sort((a, b) => a.visits - b.visits);
                
                setAllCities(parsedCities);
            } catch (err) {
                console.error("Failed to load cities", err);
            }
        };

        fetchCities();
    }, []);

    const getMarkerSize = (visits: number) => {
        if (visits > 2000) return 'w-4 h-4';
        if (visits > 500) return 'w-3 h-3';
        if (visits > 100) return 'w-2 h-2';
        return 'w-1 h-1'; // Tiny star-like dots for the mass
    };

    const getMarkerColor = (visits: number) => {
        if (visits > 2000) return 'bg-red-500 shadow-red-500/50';
        if (visits > 500) return 'bg-orange-400 shadow-orange-400/50';
        if (visits > 100) return 'bg-emerald-400 shadow-emerald-400/50'; // Bright Emerald Green for better contrast
        return 'bg-cyan-300 shadow-[0_0_4px_rgba(103,232,249,0.8)]'; // Bright cyan glowing stars
    };

    return (
        <div className="relative w-full h-[600px] rounded-xl overflow-hidden border border-blue-500/30 bg-[#020617] shadow-2xl">
            {/* 
                OPTION 1: NASA Earth at Night (Equirectangular)
                This image uses a standard Plate Carrée projection (Linear X/Y).
                It matches perfectly with direct lat/lng mapping.
            */}
            <div 
                className="absolute inset-x-0 inset-y-0"
                style={{
                    backgroundImage: `url('/earth-night.jpg')`,
                    backgroundSize: '100% 100%', // FORCE STRETCH COMPATIBILITY
                    backgroundRepeat: 'no-repeat',
                    opacity: 0.6,
                    filter: 'brightness(1.2) contrast(1.1)' // Enhance visibility
                }}
            >
                {/* Fallback Grid if image fails to load */}
                <div className="w-full h-full bg-[linear-gradient(rgba(0,40,80,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(0,40,80,0.1)_1px,transparent_1px)] bg-[size:4%_8%]" />
            </div>

            {/* Latitude / Longitude Grid Overlay (To prove alignment) */}
            <div className="absolute inset-0 pointer-events-none opacity-20">
                 {/* Equator */}
                 <div className="absolute top-1/2 left-0 right-0 h-px bg-blue-500" />
                 {/* Prime Meridian */}
                 <div className="absolute left-1/2 top-0 bottom-0 w-px bg-blue-500" />
            </div>

            {/* Markers Container */}
            <div className="absolute inset-0">
                {allCities.map((city, index) => {
                    // LINEAR PROJECTION FORMULA (Matches NASA Maps)
                    // X: -180 to 180 maps to 0 to 100%
                    const x = ((city.lng + 180) / 360) * 100;
                    
                    // Y: 90 to -90 maps to 0 to 100%
                    const y = ((90 - city.lat) / 180) * 100;

                    return (
                        <div
                            key={index}
                            className="absolute -translate-x-1/2 -translate-y-1/2 group cursor-pointer"
                            style={{ 
                                left: `${x}%`, 
                                top: `${y}%`,
                                zIndex: 10 
                            }}
                            onMouseEnter={() => setSelectedCity(city)}
                            onMouseLeave={() => setSelectedCity(null)}
                        >
                            {/* Glow */}
                            <div className={`absolute inset-0 rounded-full animate-ping opacity-75 ${getMarkerColor(city.visits).split(' ')[0]}`} />
                            
                            {/* Dot */}
                            <div className={`relative rounded-full shadow-lg ${getMarkerSize(city.visits)} ${getMarkerColor(city.visits)} border border-white/20`} />

                            {/* Tooltip */}
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-50">
                                <div className="bg-slate-900 border border-slate-700 text-white text-xs px-3 py-2 rounded shadow-xl whitespace-nowrap">
                                    <div className="font-bold">{city.name}</div>
                                    {/* Only show visits for Levels 3 & 4 (> 500) */}
                                    {city.visits > 500 && (
                                        <div className="text-emerald-400 font-mono">{city.visits.toLocaleString()} Visits</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Stats Card Overlay - Nano HUD */}
            <div className="absolute bottom-12 left-6 bg-slate-900/80 backdrop-blur-md border border-slate-700/50 p-2.5 rounded-lg shadow-2xl z-20 min-w-[160px] animate-in fade-in slide-in-from-left duration-700">
                 <div className="flex items-center justify-between mb-2">
                    <div className="flex items-baseline gap-1.5">
                        <span className="text-xl font-bold text-white tracking-tight">61</span>
                        <span className="text-slate-500 text-[9px] font-bold uppercase tracking-wider">COUNTRIES</span>
                    </div>
                    <div className="text-[9px] font-bold text-blue-400">8,000+</div>
                 </div>
                 
                 <div className="space-y-1">
                    {[
                        { code: 'US', count: 4808, flag: 'https://flagcdn.com/w40/us.png' },
                        { code: 'MA', count: 218, flag: 'https://flagcdn.com/w40/ma.png' },
                        { code: 'GB', count: 194, flag: 'https://flagcdn.com/w40/gb.png' },
                        { code: 'CA', count: 157, flag: 'https://flagcdn.com/w40/ca.png' },
                        { code: 'HK', count: 148, flag: 'https://flagcdn.com/w40/hk.png' },
                    ].map((item, i) => (
                        <div key={i} className="flex justify-between items-center text-[10px] group hover:bg-white/5 px-1 py-0.5 rounded transition-colors">
                            <div className="flex items-center gap-2">
                                <img src={item.flag} alt={item.code} className="w-4 h-auto rounded-[1px] opacity-80 group-hover:opacity-100 transition-opacity" />
                            </div>
                            <span className="font-mono text-emerald-400 font-bold tabular-nums">{item.count.toLocaleString()}</span>
                        </div>
                    ))}
                 </div>
                 
            </div>

            {/* Source Tag */}
            <div className="absolute bottom-3 left-3 px-2 py-1 bg-black/50 rounded text-[10px] text-slate-500">
                Source: Global Trader Analytics
            </div>
        </div>
    );
}
